/**
 * SETVAR / direct-sysvar-name command parser for the command line.
 *
 * Matches three call shapes (AutoCAD parity):
 *   SETVAR DYNMODE 2           — canonical English alias
 *   -SETVAR DYNMODE 2          — command-line variant
 *   УСТПЕР DYNMODE 2           — Russian alias (AutoCAD CIS)
 *   'SETVAR DYNMODE 2          — transparent form with leading quote
 *   DYNMODE 2                  — direct name (just type the var and a value)
 *   DYNMODE                    — no value → print current value
 *
 * Returns `{ handled: true, message }` when the input matches a known
 * sysvar so the caller knows to short-circuit and not route the text
 * through the normal geometry-command resolver.
 *
 * Returns `{ handled: false, message: "" }` when the input does NOT
 * look like a sysvar command — the caller must continue with normal
 * geometry-command resolution. Crucially, unknown short words like
 * "LINE" must fall through so the geometry resolver still sees them.
 */

import { t } from "../i18n";

import {
  DYN_DEFAULTS,
  isSysvarKey,
  isValidSysvarValue,
  type SysvarKey,
} from "./sysvarSchema";
import { useSysvarStore } from "./sysvarStore";

export type SysvarCommandResult = { handled: boolean; message: string };

// SETVAR / -SETVAR / УСТПЕР / 'SETVAR form.
// ReDoS analysis: the variable-name class and the trailing value are
// both bounded quantifiers ({0,31} / {0,255}), no alternation-overlap,
// no nested quantifier. Matching time is linear in input length. The
// eslint-disable silences `safe-regex`, which false-positives on any
// regex that mixes `\s+` with a following character class.
// eslint-disable-next-line security/detect-unsafe-regex -- bounded, no backtracking
const SETVAR_RE = /^'?(?:SETVAR|УСТПЕР|-SETVAR)\s+([A-ZА-Я][A-ZА-Я0-9]{0,31})(?:\s+([^\s].{0,255}))?$/iu;
// Direct form: VARNAME [value]. Require >= 4-char name so common 1-3 char
// geometry aliases (L, C, A, PL, REC, DT, MT, etc.) cannot match. All
// AutoCAD Dynamic-Input sysvars in DYN_DEFAULTS are >= 4 characters.
// eslint-disable-next-line security/detect-unsafe-regex -- bounded, no backtracking
const DIRECT_RE = /^([A-Z][A-Z0-9]{3,31})(?:\s+([^\s].{0,255}))?$/i;

export function tryHandleSetvarCommand(text: string): SysvarCommandResult {
  const trimmed = text.trim();
  if (!trimmed) return { handled: false, message: "" };

  let nameRaw: string;
  let valueRaw: string | undefined;
  const mSet = SETVAR_RE.exec(trimmed);
  if (mSet) {
    nameRaw = mSet[1];
    valueRaw = mSet[2];
  } else {
    const mDir = DIRECT_RE.exec(trimmed);
    if (!mDir) return { handled: false, message: "" };
    nameRaw = mDir[1];
    valueRaw = mDir[2];
  }

  const name = nameRaw.toUpperCase();
  if (!isSysvarKey(name)) {
    // Unknown variable. Only respond with "unknown" when user typed the
    // explicit SETVAR keyword — otherwise a random typed word would
    // shadow the downstream command resolver.
    if (mSet) {
      return {
        handled: true,
        message: t("sysvar.unknown", { name }),
      };
    }
    return { handled: false, message: "" };
  }

  const key = name as SysvarKey;
  const store = useSysvarStore.getState();
  const current = store.values[key];

  if (valueRaw === undefined) {
    return {
      handled: true,
      message: t("sysvar.current_value", {
        name,
        value: String(current),
      }),
    };
  }

  const parsed = Number(valueRaw.trim());
  if (!isValidSysvarValue(key, parsed)) {
    return {
      handled: true,
      message: t("sysvar.invalid_value", { name }),
    };
  }

  store.setVar(key, parsed);
  return {
    handled: true,
    message: t("sysvar.feedback", { name, value: String(parsed) }),
  };
}

/**
 * Public read-only helper — not used by the command parser itself but
 * exposed for ribbon / status-bar consumers that need the list of all
 * sysvar keys the parser recognizes (e.g. auto-complete).
 * @public
 */
export function listSysvarKeys(): readonly SysvarKey[] {
  return Object.keys(DYN_DEFAULTS) as SysvarKey[];
}
